import { useTranslate } from "@opensea/next-translate"
import React from "react"
import { graphql, useFragment } from "react-relay"
import { StackedAssetMedia } from "@/components/common/media/StackedAssetMedia"
import type { DealAccept_metadata$key } from "@/lib/graphql/__generated__/DealAccept_metadata.graphql"
import type { DealAccept_receipt$key } from "@/lib/graphql/__generated__/DealAccept_receipt.graphql"
import { bn, display } from "@/lib/helpers/numberUtils"
import { useConnectedAddress } from "@/providers/Wallet/selectors"
import { TxItem } from "../components/TxItem.react"
import { useTransactionUrl } from "../hooks/useTransactionUrl"
import type { TxState } from "../types"

type DealAcceptProps = {
  receipt: DealAccept_receipt$key | null | undefined
  metadata: DealAccept_metadata$key
  state: TxState
  transactionHash: string | undefined
  nonce?: number
  createdDate?: string
}

export function DealAccept({
  receipt: receiptKey,
  metadata: metadataKey,
  state,
  transactionHash,
  nonce,
  createdDate,
}: DealAcceptProps) {
  const t = useTranslate("transactions")
  const transactionUrl = useTransactionUrl(transactionHash)

  const receipt = useFragment<DealAccept_receipt$key>(
    graphql`
      fragment DealAccept_receipt on TransactionReceiptType {
        fromAddress
        transactionFee {
          unit
          symbol
          usd
        }
      }
    `,
    receiptKey,
  )

  const { type, sentAssetQuantities, receivedAssetQuantities } =
    useFragment<DealAccept_metadata$key>(
      graphql`
        fragment DealAccept_metadata on TransactionMetadataType {
          type
          sentAssetQuantities {
            asset {
              tokenStandard
              decimals
              ...StackedAssetMedia_assets
            }
            quantity
          }
          receivedAssetQuantities {
            asset {
              tokenStandard
              decimals
              ...StackedAssetMedia_assets
            }
            quantity
          }
        }
      `,
      metadataKey,
    )

  const assetQuantities = [...receivedAssetQuantities, ...sentAssetQuantities]

  const getSentDetails = () => {
    const sentItemCount = sentAssetQuantities.filter(
      aq => aq.asset.tokenStandard !== "ERC20",
    ).length
    const sentWeth = sentAssetQuantities.find(
      aq => aq.asset.tokenStandard === "ERC20",
    )

    if (sentWeth) {
      return t(
        "txItem.dealAccept.sent.sentWeth",
        {
          one: "Sent {{sentItemCount}} item, {{sentWethAmount}} WETH.",
          other: "Sent {{sentItemCount}} items, {{sentWethAmount}} WETH.",
        },
        {
          sentItemCount: display(sentItemCount),
          sentWethAmount: display(
            bn(sentWeth.quantity, sentWeth.asset.decimals),
          ),
          count: sentItemCount,
        },
      )
    }

    return t(
      "txItem.dealAccept.sent.noWeth",
      {
        one: "Sent {{sentItemCount}} item.",
        other: "Sent {{sentItemCount}} items.",
      },
      {
        sentItemCount: display(sentItemCount),
        count: sentItemCount,
      },
    )
  }

  const getReceivedDetails = () => {
    const receivedItemCount = receivedAssetQuantities.filter(
      aq => aq.asset.tokenStandard !== "ERC20",
    ).length
    const receivedWeth = receivedAssetQuantities.find(
      aq => aq.asset.tokenStandard === "ERC20",
    )

    if (receivedWeth) {
      return t(
        "txItem.dealAccept.received.receivedWeth",
        {
          one: "Received {{receivedItemCount}} item, {{receivedWethAmount}} WETH.",
          other:
            "Received {{receivedItemCount}} items, {{receivedWethAmount}} WETH.",
        },
        {
          receivedItemCount: display(receivedItemCount),
          receivedWethAmount: display(
            bn(receivedWeth.quantity, receivedWeth.asset.decimals),
          ),
          count: receivedItemCount,
        },
      )
    }

    return t(
      "txItem.dealAccept.received.noWeth",
      {
        one: "Received {{receivedItemCount}} item.",
        other: "Received {{receivedItemCount}} items.",
      },
      {
        receivedItemCount: display(receivedItemCount),
        count: receivedItemCount,
      },
    )
  }

  const connectedAddress = useConnectedAddress()
  const isTxSender = receipt?.fromAddress === connectedAddress
  const isComplete = receipt && transactionHash
  const isPending = !receipt && transactionHash

  return (
    <TxItem href={isPending ? undefined : transactionUrl} target="_blank">
      <TxItem.Avatar state={state}>
        <StackedAssetMedia assets={assetQuantities.map(aq => aq.asset)} />
      </TxItem.Avatar>
      <TxItem.Content>
        <TxItem.Title>
          {type === "ACCEPT_SWAP"
            ? t("txItem.dealAccept.accept.title", "Accept deal")
            : t("txItem.dealAccept.accepted.title", "Deal accepted")}
        </TxItem.Title>
        <TxItem.Description>
          {getSentDetails()} {getReceivedDetails()}
        </TxItem.Description>
      </TxItem.Content>
      {isComplete ? (
        isTxSender && (
          <TxItem.Value
            symbol={receipt.transactionFee.symbol}
            unit={receipt.transactionFee.unit}
            usd={receipt.transactionFee.usd}
          />
        )
      ) : isPending ? (
        <TxItem.Pending
          createdDate={createdDate}
          href={transactionUrl}
          nonce={nonce}
        />
      ) : (
        <TxItem.Requested />
      )}
    </TxItem>
  )
}
